import { useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Robot,
  Play,
  CheckCircle,
  Clock,
  ArrowSquareOut,
  CaretDown,
  CaretUp,
  Eye,
  SpinnerGap,
} from '@phosphor-icons/react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { AIAgentRun, AIAgentMonitor } from '../../data/mockARData';

function formatCurrency(amount: number): string {
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function AgentRunCard({
  run,
  onWatchRecording,
}: {
  run: AIAgentRun;
  onWatchRecording?: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full p-3 flex items-center justify-between hover:bg-muted/50 transition-colors text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-green-100 p-1.5">
            <Robot className="h-4 w-4 text-green-700" />
          </div>
          <div>
            <p className="font-medium text-sm">Dispute Submitted</p>
            <p className="text-xs text-muted-foreground">
              {run.invoiceNumber} • {formatCurrency(run.amountDisputed || 0)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </Badge>
          {isExpanded ? (
            <CaretUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <CaretDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="p-3 border-t bg-muted/30 space-y-3">
          <p className="text-sm text-muted-foreground">{run.description}</p>

          {run.result && (
            <div className="rounded-lg bg-green-50 border-green-200 border p-2.5">
              <p className="text-xs font-medium text-green-800">Result</p>
              <p className="text-sm text-green-700">{run.result}</p>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{format(new Date(run.startedAt), 'MMM d, yyyy h:mm a')}</span>
          </div>

          <div className="flex gap-2">
            {run.videoUrl && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={onWatchRecording}
              >
                <Play className="h-3 w-3" />
                Watch Recording
              </Button>
            )}
            {run.portalUrl && (
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 text-muted-foreground"
                asChild
              >
                <a
                  href={run.portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ArrowSquareOut className="h-3 w-3" />
                  View Portal
                </a>
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusMonitorCard({ monitor }: { monitor: AIAgentMonitor }) {
  const [showAllLogs, setShowAllLogs] = useState(false);
  const logsToShow = showAllLogs
    ? monitor.checksLog
    : monitor.checksLog.slice(0, 3);

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="p-3 bg-blue-50/50 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-blue-100 p-1.5">
              <Eye className="h-4 w-4 text-blue-700" />
            </div>
            <div>
              <p className="font-medium text-sm">{monitor.title}</p>
              <p className="text-xs text-muted-foreground">
                {monitor.description}
              </p>
            </div>
          </div>
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <SpinnerGap className="h-3 w-3 mr-1 animate-spin" />
            Active
          </Badge>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Schedule Info */}
        <div className="flex items-center justify-between text-sm">
          <div>
            <span className="text-muted-foreground">Check frequency:</span>{' '}
            <span className="font-medium">{monitor.checkFrequency}</span>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-amber-50 border-amber-200 border p-2.5">
          <div>
            <p className="text-xs font-medium text-amber-800">Next Check</p>
            <p className="text-sm font-medium text-amber-700">
              {format(new Date(monitor.nextCheckAt), 'MMM d, yyyy h:mm a')}
            </p>
          </div>
          <div className="text-xs text-amber-600">
            {formatDistanceToNow(new Date(monitor.nextCheckAt), {
              addSuffix: true,
            })}
          </div>
        </div>

        {/* Check Log */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Check History ({monitor.checksLog.length} checks)
          </p>
          <div className="space-y-1.5">
            {logsToShow.map((log, idx) => (
              <div
                key={idx}
                className={cn(
                  'flex items-start gap-2 text-xs p-2 rounded',
                  log.status === 'response_received'
                    ? 'bg-green-50'
                    : log.status === 'error'
                      ? 'bg-red-50'
                      : 'bg-gray-50',
                )}
              >
                <div
                  className={cn(
                    'rounded-full p-0.5 mt-0.5',
                    log.status === 'response_received'
                      ? 'bg-green-200'
                      : log.status === 'error'
                        ? 'bg-red-200'
                        : 'bg-gray-200',
                  )}
                >
                  {log.status === 'response_received' ? (
                    <CheckCircle className="h-2.5 w-2.5 text-green-700" />
                  ) : log.status === 'error' ? (
                    <Clock className="h-2.5 w-2.5 text-red-700" />
                  ) : (
                    <Clock className="h-2.5 w-2.5 text-gray-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {format(new Date(log.timestamp), 'MMM d, h:mm a')}
                    </span>
                  </div>
                  <p className="text-gray-700">{log.message}</p>
                </div>
              </div>
            ))}
          </div>

          {monitor.checksLog.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 text-xs"
              onClick={() => setShowAllLogs(!showAllLogs)}
            >
              {showAllLogs
                ? 'Show less'
                : `Show ${monitor.checksLog.length - 3} more checks`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface AIAgentActivityPanelProps {
  runs: AIAgentRun[];
  monitors: AIAgentMonitor[];
  onWatchRecording?: (videoUrl: string) => void;
}

export function AIAgentActivityPanel({
  runs,
  monitors,
  onWatchRecording,
}: AIAgentActivityPanelProps) {
  if (runs.length === 0 && monitors.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <Robot className="h-4 w-4" />
          AI Agent Activity
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {runs.map((run) => (
          <AgentRunCard
            key={run.id}
            run={run}
            onWatchRecording={
              run.videoUrl ? () => onWatchRecording?.(run.videoUrl!) : undefined
            }
          />
        ))}
        {monitors.map((monitor) => (
          <StatusMonitorCard key={monitor.id} monitor={monitor} />
        ))}
      </CardContent>
    </Card>
  );
}
